import { useState, useEffect } from 'react';

import CustomModal from '@/components/CustomModal';
import DatePicker from '@/components/DatePicker';
import Select from '@/components/Select';
import { DeletedRecordTypeOptions } from '@/constants/constant';
import { ERestoreEntityType } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';

export type TDeletedRecordsFilters = {
  type: ERestoreEntityType[] | undefined;
  startDate: string | null;
  endDate: string | null;
};

interface IDeletedRecordsFilterDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialFilters: TDeletedRecordsFilters;
  onApplyFilters: (filters: TDeletedRecordsFilters) => void;
  dataTestId?: string;
}

const DeletedRecordsFilterDrawer = ({
  isOpen,
  setIsOpen,
  initialFilters,
  onApplyFilters,
  dataTestId,
}: IDeletedRecordsFilterDrawerProps) => {
  const { formatText } = useFormatText();

  const [type, setType] = useState<ERestoreEntityType[] | undefined>(
    initialFilters?.type
  );
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setType(initialFilters.type);
      setStartDate(initialFilters.startDate);
      setEndDate(initialFilters.endDate);
    }
  }, [
    initialFilters.endDate,
    initialFilters.startDate,
    initialFilters.type,
    isOpen,
  ]);

  const handleClearFilters = () => {
    setType(undefined);
    setStartDate(null);
    setEndDate(null);
    onApplyFilters({
      type: undefined,
      startDate: null,
      endDate: null,
    });
  };

  const handleApply = () => {
    onApplyFilters({
      type,
      startDate,
      endDate,
    });
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      position="right"
      isHeader
      heading={formatText('common.actions.filter')}
      isFooter
      cancelButtonProps={{
        variant: 'link',
        label: formatText('common.actions.clear-filters'),
        onClick: handleClearFilters,
        className: 'underline text-brand-secondary font-normal',
        dataTestId: dataTestId ? `${dataTestId}-clear` : undefined,
      }}
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        onClick: handleApply,
        variant: 'primary',
        dataTestId: dataTestId ? `${dataTestId}-apply` : undefined,
      }}
      dataTestId={dataTestId}
    >
      <div
        className="w-full flex flex-col gap-6"
        data-testid={dataTestId ? `${dataTestId}-form` : undefined}
      >
        {/* Type Filter */}
        <div className="w-full">
          <Select
            name={formatText('settings.deleted-records.filters.type')}
            options={DeletedRecordTypeOptions}
            selected={type ? type?.map(String) : []}
            onChange={(val) =>
              setType(
                val
                  ? ((val as string[])?.map(Number) as ERestoreEntityType[])
                  : undefined
              )
            }
            placeholder={formatText(
              'settings.deleted-records.filters.type-placeholder'
            )}
            rootClassName="w-full"
            isClearable
            isTextFormat
            isMulti
            dataTestId={dataTestId ? `${dataTestId}-type-select` : undefined}
          />
        </div>

        {/* Start Date Filter */}
        <div className="w-full">
          <DatePicker
            label={formatText('settings.deleted-records.filters.start-date')}
            placeholder={formatText(
              'settings.deleted-records.filters.start-date-placeholder'
            )}
            value={startDate}
            onChange={setStartDate}
            dataTestId={dataTestId ? `${dataTestId}-start-date` : undefined}
          />
        </div>

        {/* End Date Filter */}
        <div className="w-full">
          <DatePicker
            label={formatText('settings.deleted-records.filters.end-date')}
            placeholder={formatText(
              'settings.deleted-records.filters.end-date-placeholder'
            )}
            value={endDate}
            onChange={setEndDate}
            dataTestId={dataTestId ? `${dataTestId}-end-date` : undefined}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default DeletedRecordsFilterDrawer;
